/**
 * Derives per-agent "Ops Score" and RESOLUTION/CONSISTENCY/COVERAGE/DEPTH axes
 * from daily summaries + KPI. Replaces keyword-counting with outcome-based scoring.
 */
import type { DailySummaryItem } from '../../hooks/useMemorySummary';

export type CompetencyAxisId = 'resolution' | 'consistency' | 'coverage' | 'depth';

export type CompetencyStage = 'learning' | 'competent' | 'proficient';

export type AgentCompetencyMetrics = {
  axes: Record<CompetencyAxisId, number>;
  composite: number;
  score: number;
  scoreDelta: number;
  peak: number;
  trough: number;
  stage: CompetencyStage;
  stageTarget: number;
  trend: { date: string; fullDate: string; value: number }[];
  /** True when scores are mostly from daily memory summaries, not seed/KPI only. */
  fromMemorySummaries: boolean;
};

const AXIS_LABELS: Record<CompetencyAxisId, string> = {
  resolution: 'RESOLUTION',
  consistency: 'CONSISTENCY',
  coverage: 'COVERAGE',
  depth: 'DEPTH',
};

export function competencyAxisLabel(id: CompetencyAxisId): string {
  return AXIS_LABELS[id];
}

function hashAgentId(agentId: string): number {
  let h = 0;
  for (let i = 0; i < agentId.length; i += 1) {
    h = (Math.imul(31, h) + agentId.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function seedAxes(agentId: string): Record<CompetencyAxisId, number> {
  const h = hashAgentId(agentId);
  return {
    resolution: 52 + (h % 38),
    consistency: 48 + ((h >> 6) % 40),
    coverage: 50 + ((h >> 12) % 36),
    depth: 54 + ((h >> 18) % 34),
  };
}

function clamp(n: number, min = 0, max = 100): number {
  return Math.min(max, Math.max(min, n));
}

function avg(nums: number[]): number {
  if (!nums.length) return 0;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

function hasAny(text: string, keywords: string[]): boolean {
  return keywords.some((kw) => text.includes(kw));
}

function tokenize(md: string): string[] {
  return md
    .toLowerCase()
    .split(/[\s,.:;()[\]{}"'\r\n]+/)
    .filter((t) => t.length > 2);
}

const ISSUE_KEYWORDS = ['alarm', 'ovl', 'แจ้งเตือน', 'เกินเกณฑ์', 'ผิดปกติ', 'เกิดปัญหา', 'error', 'fault'];
const RESOLVE_KEYWORDS = ['แก้ไข', 'ตรวจสอบแล้ว', 'กลับสู่ปกติ', 'กลับมาปกติ', 'คลี่คลาย', 'resolved', 'cleared', 'normalised', 'normalized', 'ปกติแล้ว'];
const EQUIPMENT_KEYWORDS = ['pump', 'ปั๊ม', 'sensor', 'mixer', 'adder', 'valve', 'วาล์ว', 'มอเตอร์', 'motor', 'อุปกรณ์', 'equipment', 'compressor', 'blower', 'feeder'];
const ROOT_CAUSE_KEYWORDS = ['สาเหตุ', 'เนื่องจาก', 'เพราะ', 'root cause', 'because', 'caused by', 'due to', 'เกิดจาก'];

function scoreFromDaily(dailyItems: DailySummaryItem[]): Record<CompetencyAxisId, number> {
  const sorted = [...dailyItems].sort((a, b) => a.summary_date.localeCompare(b.summary_date));
  const last30 = sorted.slice(-30);

  // --- resolution: fraction of issue-days that also have a resolve keyword ---
  let daysWithIssue = 0;
  let daysResolved = 0;
  last30.forEach((d) => {
    const md = (d.summary_md || '').toLowerCase();
    if (hasAny(md, ISSUE_KEYWORDS)) {
      daysWithIssue += 1;
      if (hasAny(md, RESOLVE_KEYWORDS)) daysResolved += 1;
    }
  });
  // No issues at all → healthy baseline (not perfect — no evidence of active resolution)
  const resolution = daysWithIssue === 0
    ? 85
    : clamp(Math.round((daysResolved / daysWithIssue) * 100));

  // --- consistency: distinct days / expected span (max 30) ---
  const distinctDates = new Set(last30.map((d) => d.summary_date));
  let spanDays = 30;
  if (last30.length >= 2) {
    const first = new Date(last30[0].summary_date).getTime();
    const last = new Date(last30[last30.length - 1].summary_date).getTime();
    spanDays = Math.min(30, Math.round((last - first) / 86_400_000) + 1);
  } else if (last30.length === 1) {
    spanDays = 1;
  }
  const consistency = clamp(Math.round((distinctDates.size / spanDays) * 100));

  // --- coverage: distinct equipment tokens mentioned across all days ---
  const equipMentioned = new Set<string>();
  last30.forEach((d) => {
    const md = (d.summary_md || '').toLowerCase();
    EQUIPMENT_KEYWORDS.forEach((kw) => {
      if (md.includes(kw)) equipMentioned.add(kw);
    });
  });
  const coverage = clamp(40 + equipMentioned.size * 12);

  // --- depth: novelty ratio (tokens not seen in prior 7 days) + root-cause bonus ---
  let totalNovelRatio = 0;
  let rootCauseHits = 0;
  last30.forEach((d, i) => {
    const md = (d.summary_md || '').toLowerCase();
    if (hasAny(md, ROOT_CAUSE_KEYWORDS)) rootCauseHits += 1;

    const prior7 = last30.slice(Math.max(0, i - 7), i);
    const priorTokens = new Set(prior7.flatMap((p) => tokenize(p.summary_md || '')));
    const todayTokens = tokenize(md);
    if (todayTokens.length === 0) {
      totalNovelRatio += 0;
    } else {
      const novel = todayTokens.filter((t) => !priorTokens.has(t)).length;
      totalNovelRatio += novel / todayTokens.length;
    }
  });
  const avgNovelRatio = last30.length ? totalNovelRatio / last30.length : 0;
  const depth = clamp(Math.round(35 + avgNovelRatio * 45 + rootCauseHits * 6));

  return { resolution, consistency, coverage, depth };
}

function buildTrend(
  dailyItems: DailySummaryItem[],
  scoreBase: number,
): AgentCompetencyMetrics['trend'] {
  const sorted = [...dailyItems]
    .sort((a, b) => a.summary_date.localeCompare(b.summary_date))
    .slice(-30);
  if (!sorted.length) {
    const today = new Date();
    return Array.from({ length: 14 }, (_, i) => {
      const d = new Date(today);
      d.setDate(today.getDate() - (13 - i));
      const iso = d.toISOString().slice(0, 10);
      const wobble = Math.sin(i * 0.7) * 4;
      return {
        date: iso.slice(5),
        fullDate: iso,
        value: Math.round(scoreBase + wobble),
      };
    });
  }
  return sorted.map((d, i) => {
    const turns = d.source_turn_count || 0;
    const value = Math.round(scoreBase - 12 + turns * 0.55 + (i % 5));
    return {
      date: d.summary_date.slice(5),
      fullDate: d.summary_date,
      value: clamp(value, 50, 100),
    };
  });
}

function stageFromComposite(composite: number): {
  stage: CompetencyStage;
  stageTarget: number;
} {
  if (composite >= 85) {
    return { stage: 'proficient', stageTarget: 95 };
  }
  if (composite >= 70) {
    return { stage: 'competent', stageTarget: 85 };
  }
  return { stage: 'learning', stageTarget: 70 };
}

export function computeAgentCompetencyMetrics(
  agentId: string,
  dailyItems: DailySummaryItem[],
  kpiScore?: number | null,
): AgentCompetencyMetrics {
  const seed = seedAxes(agentId);
  const fromDaily = dailyItems.length >= 3 ? scoreFromDaily(dailyItems) : null;

  const blend = (axis: CompetencyAxisId): number => {
    if (!fromDaily) return seed[axis];
    return Math.round(seed[axis] * 0.25 + fromDaily[axis] * 0.75);
  };

  const axes: Record<CompetencyAxisId, number> = {
    resolution: blend('resolution'),
    consistency: blend('consistency'),
    coverage: blend('coverage'),
    depth: blend('depth'),
  };

  const composite = Math.round((axes.resolution + axes.consistency + axes.coverage + axes.depth) / 4);
  const kpi = kpiScore != null && !Number.isNaN(Number(kpiScore)) ? Number(kpiScore) : null;
  const score = Math.round(composite * 0.92 + (kpi ?? composite) * 0.08);

  const trend = buildTrend(dailyItems, score);
  const values = trend.map((t) => t.value);
  const peak = values.length ? Math.max(...values) : score;
  const trough = values.length ? Math.min(...values) : score;

  const last7 = values.slice(-7);
  const prev7 = values.slice(-14, -7);
  const scoreDelta =
    last7.length && prev7.length
      ? Math.round(avg(last7) - avg(prev7))
      : Math.round((hashAgentId(agentId) % 20) - 5);

  const { stage, stageTarget } = stageFromComposite(composite);
  const fromMemorySummaries = dailyItems.length >= 3;

  return {
    axes,
    composite,
    score,
    scoreDelta,
    peak,
    trough,
    stage,
    stageTarget,
    trend,
    fromMemorySummaries,
  };
}

export const COMPETENCY_AXIS_ORDER: CompetencyAxisId[] = ['resolution', 'consistency', 'coverage', 'depth'];

export const COMPETENCY_AXIS_COLORS: Record<CompetencyAxisId, string> = {
  resolution: '#22c55e',
  consistency: '#3b82f6',
  coverage: '#a855f7',
  depth: '#e2e8f0',
};
